import { useMemo } from 'react';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import moment from 'moment-timezone';
import * as Yup from 'yup';

import {
  PaginatedAvailableDates,
  Participation,
  useParticipant,
  usePublicSession,
} from '@app/api';

import { Button, CenteredButton, CenteredLoader } from '@app/core';
import { NotAvailableButton } from './NotAvailableButton';
import { Form, Formik } from 'formik';
import { Trans, useTranslation } from 'react-i18next';
import { DateSelectionField, TimezoneSelect } from '@app/form';

const useStyles = createUseStyles((theme: Theme) => ({
  section: {
    marginTop: theme.marginBase * 7,
    marginBottom: theme.marginBase * 7,

    [theme.media.mobile]: {
      marginBottom: theme.marginBase * 2,
    },
  },
  notAvailable: {
    marginRight: theme.marginBase,
  },
  subTitle: {
    ...theme.fonts.h4,
    marginBottom: theme.marginBase * 2,
  },
  timezone: {
    backgroundColor: theme.colors.grayBackground,
    padding: theme.marginBase * 2,
    maxWidth: theme.maxContentWidth.reducedFormContent,
    marginBottom: theme.marginBase * 7,
  },
  loader: {
    marginBottom: theme.marginBase * 4,
  },
}));

export interface MeetingDateSelectionFormValues {
  date: string;
  timezone?: string;
}

interface Props {
  onSubmit: (values: MeetingDateSelectionFormValues) => any;
  participation: Participation;
  submitText: string;
  paginatedAvailableDates: PaginatedAvailableDates;
}

const validationSchema = Yup.object().shape({
  date: Yup.string().required(),
});

export const MeetingDateSelectionForm = ({
  participation,
  onSubmit,
  submitText,
  paginatedAvailableDates: { availableDates, hasNextPage, fetchNextPage },
}: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const { data: session } = usePublicSession(participation.sessionId);

  const {
    data: participant,
    isLoading,
    isFetched,
  } = useParticipant(participation.participantId, { retry: false });

  const initialvalue = useMemo(
    () => ({
      date: '',
      timezone: participant?.timezone || moment.tz.guess(),
    }),
    [participant],
  );

  if (!session || (isLoading && !isFetched)) {
    return <CenteredLoader />;
  }

  return (
    <Formik
      initialValues={initialvalue}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
    >
      <Form>
        <section className={classes.section}>
          <h2 className={classes.subTitle}>
            {t('booking.appointment.title', { duration: session.duration })}
          </h2>

          <div className={classes.timezone}>
            <TimezoneSelect
              label={t('booking.timezone', { timezone: '' })}
              name="timezone"
              noMargin
            />
          </div>

          {availableDates ? (
            <DateSelectionField
              duration={session.duration}
              dates={availableDates}
              name="date"
            />
          ) : (
            <CenteredLoader />
          )}
        </section>

        <CenteredButton submitButton text={submitText}>
          {hasNextPage ? (
            <Button
              variant="muted"
              onClick={() => fetchNextPage()}
              className={classes.notAvailable}
            >
              <Trans i18nKey="booking.appointment.showMoreAvailabilities" />
            </Button>
          ) : (
            <NotAvailableButton
              participation={participation}
              className={classes.notAvailable}
              sessionId={session.id}
            />
          )}
        </CenteredButton>
      </Form>
    </Formik>
  );
};
